import { memo, useCallback, useMemo, useState } from 'react';

import Table from '@components/common/Table/Table';

import { useMutateDisableAssetAccess } from '@hooks/assets/assetAccess/useMutateDisableAssetAccess';
import { useMutateEnableAssetAccess } from '@hooks/assets/assetAccess/useMutateEnableAssetAccess';
import { useQueryAccessAccessAoVisibilityModes } from '@hooks/assets/assetAccess/useQueryAccessAccessAoVisibilityModes';
import { useQueryAssetAccessRestricted } from '@hooks/assets/assetAccess/useQueryAssetAccessRestricted';
import { useQueryResultingAssetAccessTypes } from '@hooks/assets/assetAccess/useQueryResultingAssetAccessTypes';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';

import { defaultMeta } from '@enums/common';

import { booleanOptions, MIN_FILTER_WIDTH } from '@constants/common';
import { TableFilterTypes } from '@constants/table';

import { getColumns } from './utils';

import styles from '../../OrganisationProfile.module.scss';

interface IAOAccessTableProps {
	id: string;
}

const AOAccessTable = ({ id }: IAOAccessTableProps) => {
	const {
		accessPermission: {
			platformOwnerAdministrator,
			assetOwnerOrganizationAdministrator,
		},
	} = useAppSelector(auth);

	const isToggleAccessAvailable = platformOwnerAdministrator || assetOwnerOrganizationAdministrator;

	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [searchValue, setSearchValue] = useState('');
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);

	const [selectedAccessStatus, setSelectedAccessStatus] = useState<TSelectOption<string> | null>(null);
	const [selectedAOVisibilityMode, setSelectedAOVisibilityMode] = useState<TSelectOption<string> | null>(null);
	const [
		selectedAssetResultingAccess,
		setSelectedAssetResultingAccess,
	] = useState<TSelectOption<string> | null>(null);

	const {
		assetAccessRestricted,
		countOfRecords,
		isLoadingAssetAccessRestricted,
	} = useQueryAssetAccessRestricted({
		id,
		page,
		pageSize,
		searchValue,
		sortDirection,
		sortFieldName,
		accessStatus: selectedAccessStatus?.value,
		resultingAccess: selectedAssetResultingAccess?.value,
		aoVisibilityMode: selectedAOVisibilityMode?.value,
	});

	const {
		assetAccessResultingTypes,
		isLoadingAssetAccessResultingTypes,
	} = useQueryResultingAssetAccessTypes();

	const {
		accessAOVisibilityModes,
		isLoadingAccessAOVisibilityModes,
	} = useQueryAccessAccessAoVisibilityModes();

	const {
		enableAssetAccess,
		isLoadingEnableAssetAccess,
	} = useMutateEnableAssetAccess();

	const {
		disableAssetAccess,
		isLoadingDisableAssetAccess,
	} = useMutateDisableAssetAccess();

	const handleAssetAccessChange = useCallback((value: TSelectOption<string>) => {
		setSelectedAccessStatus(value);
		setPage(defaultMeta.page);
	}, []);

	const handleAssetVisibilityChange = useCallback((value: TSelectOption<string>) => {
		setSelectedAOVisibilityMode(value);
		setPage(defaultMeta.page);
	}, []);

	const handleAssetResultingAccessChange = useCallback((value: TSelectOption<string>) => {
		setSelectedAssetResultingAccess(value);
		setPage(defaultMeta.page);
	}, []);

	const toggleAccess = useCallback((organizationId: string, isEnabled: boolean) => {
		if (!isToggleAccessAvailable) {
			return;
		}

		if (isEnabled) {
			disableAssetAccess({ id, organizationId });
		} else {
			enableAssetAccess({ id, organizationId });
		}
	}, [
		enableAssetAccess,
		disableAssetAccess,
		isToggleAccessAvailable,
		id,
	]);

	const filters: TTableFilter[] = useMemo(() => {
		return [
			{
				name: 'accessStatus',
				placeholder: 'Access status',
				option: selectedAccessStatus,
				options: booleanOptions,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleAssetAccessChange,
				defaultValue: null,
			},
			{
				name: 'visibilityType',
				placeholder: 'AO Visibility Mode',
				option: selectedAOVisibilityMode,
				options: accessAOVisibilityModes,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleAssetVisibilityChange,
				defaultValue: null,
			},
			{
				name: 'resultingAccess',
				placeholder: 'Resulting access',
				option: selectedAssetResultingAccess,
				options: assetAccessResultingTypes,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleAssetResultingAccessChange,
				defaultValue: null,
			},
		];
	}, [
		assetAccessResultingTypes,
		accessAOVisibilityModes,
		selectedAssetResultingAccess,
		selectedAOVisibilityMode,
		selectedAccessStatus,
		handleAssetResultingAccessChange,
		handleAssetVisibilityChange,
		handleAssetAccessChange,
	]);

	const columns = useMemo(() => {
		return getColumns({ toggleAccess, isToggleAccessAvailable });
	}, [toggleAccess, isToggleAccessAvailable]);

	const pageHandler = useCallback((page: number) => setPage(page), []);
	const pageSizeHandler = useCallback((pageSize: number) => {
		setPageSize(pageSize);
		setPage(defaultMeta.page);
	}, []);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
		setPage(defaultMeta.page);
	}, []);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const searchHandler = useCallback((searchValue: string) => {
		setSearchValue(searchValue);
		setPage(defaultMeta.page);
	}, []);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords: countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	}), [
		page,
		pageSize,
		countOfRecords,
		pageHandler,
		pageSizeHandler,
	]);

	const search: ITableSearch[] = useMemo(() => [{
		initialValue: searchValue,
		name: 'name',
		handler: searchHandler,
		tooltipText: 'Search by name',
	}], [searchValue, searchHandler]);

	const isLoading = isLoadingAccessAOVisibilityModes
		|| isLoadingAssetAccessRestricted
		|| isLoadingAssetAccessResultingTypes
		|| isLoadingEnableAssetAccess
		|| isLoadingDisableAssetAccess;

	return (
		<div className={styles.tableWrapper}>
			<Table
				columns={columns}
				data={assetAccessRestricted}
				filters={filters}
				isLoading={isLoading}
				isWithoutCheckbox
				isWithoutDeleteAction
				pagination={pagination}
				search={search}
				sort={sort}
			/>
		</div>
	);
};

export default memo(AOAccessTable);
